import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { KyrgyzstanDetailContent } from 'widgets/kyrgyzstan/detail-content'
import { KyrgyzstanDetailShell } from 'widgets/kyrgyzstan/detail-shell'

import {
  getEffectiveStayParams,
  getKyrgyzstanGalleryRouteState,
  getKyrgyzstanMapRouteState,
} from 'entities/kyrgyzstan/lib'
import { getDestinationTitle } from 'entities/kyrgyzstan/lib/get-destination-title'
import { getKyrgyzstanDetailSubtitle } from 'entities/kyrgyzstan/lib/get-kyrgyzstan-detail-subtitle'
import {
  kyrgyzstanDetailContextStore,
  kyrgyzstanDetailSelectionStore,
} from 'entities/kyrgyzstan/model'
import { getShareUrl } from 'entities/tour/ui/appeal-details/libs/getShareUrl'

const KyrgyzstanDetailViewPage = observer(() => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>() as { id: string }
  const { t } = useTranslation()

  const { selectedParent, selectedChild } = kyrgyzstanDetailSelectionStore

  const currentParentDetail = selectedParent?.id === id ? selectedParent : null
  const currentChildDetail =
    selectedChild?.parent_id === id ? selectedChild : null
  const selectedChildren = currentChildDetail
    ? [{ ...currentChildDetail, isSelected: true }]
    : []
  const effectiveStayParams = getEffectiveStayParams({
    searchParams: kyrgyzstanDetailContextStore.detailSearchParams,
    realty: currentParentDetail,
  })

  const handleGoBack = () => {
    const hasPreviousPages =
      window.history.length > 1 &&
      (document.referrer.includes(window.location.origin) ||
        window.history.length > 2)

    if (hasPreviousPages) {
      navigate(-1)
    } else {
      navigate(RoutingPaths.kyrgyzstan.booking.path())
    }
  }

  const handleOpenGallery = (parentId: string) => {
    if (currentParentDetail?.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.gallery.path(parentId), {
      state: getKyrgyzstanGalleryRouteState(currentParentDetail),
    })
  }

  const handleOpenFullMap = (parentId: string) => {
    if (currentParentDetail?.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.map.path(parentId), {
      state: getKyrgyzstanMapRouteState(currentParentDetail),
    })
  }

  const shareUrl = getShareUrl(RoutingPaths.kyrgyzstan.detail.path(id))
  const detailError = !currentParentDetail
    ? t('kyrgyzstan_block.error.title')
    : null
  const detailSubtitle = getKyrgyzstanDetailSubtitle({
    searchParams: effectiveStayParams,
    t,
  })

  return (
    <KyrgyzstanDetailShell
      header={{
        title: currentParentDetail
          ? getDestinationTitle(currentParentDetail)
          : undefined,
        subtitle: detailSubtitle,
        onGoBack: handleGoBack,
      }}
      share={
        currentParentDetail
          ? {
              url: shareUrl,
              text: currentParentDetail.title,
            }
          : undefined
      }
      state={{
        isLoading: false,
        error: detailError,
      }}
    >
      {currentParentDetail && (
        <KyrgyzstanDetailContent
          realty={currentParentDetail}
          stayParams={effectiveStayParams}
          childrenList={selectedChildren}
          childrenTitle={t('kyrgyzstan_block.detail.selected_room_title')}
          isChildrenListLoading={false}
          onOpenDates={() =>
            navigate(RoutingPaths.kyrgyzstan.calendarBooking.path())
          }
          onOpenGuests={() =>
            navigate(RoutingPaths.kyrgyzstan.selectGuestsBooking.path())
          }
          onOpenGallery={handleOpenGallery}
          onOpenMap={handleOpenFullMap}
        />
      )}
    </KyrgyzstanDetailShell>
  )
})

export default KyrgyzstanDetailViewPage
